import {
  type ImporterReliabilityData,
  type ImportFailureItem,
  type ImportJobTermination,
  type JobTerminationCategory,
  MAX_IMPORT_FAILURE_ITEMS,
  type PerItemFailureCategory,
} from '~/components/ImportHistory/types';

const REASON_LABELS: Record<string, string> = {
  NOT_FOUND: 'Not found',
  PERMISSION_DENIED: 'Permission denied',
  FORBIDDEN: 'Access forbidden',
  GATEWAY_TIMEOUT: 'Gateway timeout',
  CONNECTION_TIMEOUT: 'Connection timeout',
  TOO_MANY_REQUEST: 'Rate limited',
  RESOURCE_FAIL: 'Server error',
  CONFLICT_FAIL: 'Conflict',
  INCOMPLETE_DATA: 'Incomplete data',
  CORRUPTED_SOURCE_FILE: 'Corrupted source file',
  TOKEN_EXPIRED: 'Token expired',
  NO_MORE_STORAGE: 'Storage limit reached',
  NO_WORKSPACE_PERMISSION: 'No workspace permission',
  NO_IMPORT_SESSION: 'Import session expired',
  UNEXPECTED_ERROR: 'Unexpected error',
  ZERO_SIZED_FILE: 'Zero-byte file',
  TIMEOUT: 'Timed out',
  OUT_OF_MEMORY: 'Out of memory',
  MAX_ATTEMPTS: 'Max attempts reached',
  TOO_MANY_FAILURES: 'Too many failures',
};

const OPERATIONS = [
  'IMPORTER_DOWNLOAD_FILE_ERROR',
  'IMPORTER_TRAVERSING_FILES_ERROR',
  'IMPORTER_TRAVERSING_FOLDERS_ERROR',
] as const;

const OPERATION_CONTEXT_LABELS: Record<(typeof OPERATIONS)[number], string> = {
  IMPORTER_DOWNLOAD_FILE_ERROR: 'file download',
  IMPORTER_TRAVERSING_FILES_ERROR: 'file crawl',
  IMPORTER_TRAVERSING_FOLDERS_ERROR: 'folder crawl',
};

/**
 * Formats just the reason portion of a failure code.
 * Use when the operation context is already provided by the section.
 *
 * Input:  "IMPORTER_DOWNLOAD_FILE_ERROR_NOT_FOUND"
 * Output: "Not found"
 */
export const formatFailureReason = (code: string): string => {
  const operation = OPERATIONS.find((op) => code.startsWith(op));

  if (operation) {
    const reason = code.slice(operation.length + 1);

    return REASON_LABELS[reason] ?? titleCase(reason);
  }

  return REASON_LABELS[code] ?? titleCase(code);
};

/**
 * Legacy `task.data.failures` keys include the operation prefix. Keep that
 * context in the label so file vs folder codes don't collapse to the same text
 * after consolidating into one Failures list.
 *
 * Input:  "IMPORTER_DOWNLOAD_FILE_ERROR_NOT_FOUND"
 * Output: "Not found (file download)"
 */
export const formatLegacyFailureLabel = (code: string): string => {
  const operation = OPERATIONS.find((op) => code.startsWith(op));

  if (operation) {
    const reason = code.slice(operation.length + 1);
    const reasonLabel = REASON_LABELS[reason] ?? titleCase(reason);

    return `${reasonLabel} (${OPERATION_CONTEXT_LABELS[operation]})`;
  }

  return REASON_LABELS[code] ?? titleCase(code);
};

/** "SOME_ERROR_CODE" → "Some error code" */
const titleCase = (str: string): string => {
  const lower = str.toLowerCase().replace(/_/g, ' ');

  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readOptionalNumber = (record: Record<string, unknown>, key: string): number | undefined => {
  const value = record[key];

  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const PER_ITEM_FAILURE_CATEGORIES: ReadonlySet<string> = new Set<PerItemFailureCategory>([
  'retryableTransient',
  'retryableTimeout',
  'terminalPermission',
  'terminalBadSourceData',
  'unknown',
]);

const isPerItemFailureCategory = (value: string): value is PerItemFailureCategory =>
  PER_ITEM_FAILURE_CATEGORIES.has(value);

const JOB_TERMINATION_CATEGORIES: ReadonlySet<string> = new Set<JobTerminationCategory>([
  'jobTooManyFailures',
  'jobCapacity',
  'jobSession',
  'jobPermission',
  'jobTimeout',
  'jobResource',
  'jobMaxAttempts',
]);

const isJobTerminationCategory = (value: string): value is JobTerminationCategory =>
  JOB_TERMINATION_CATEGORIES.has(value);

const isFailureItemType = (value: unknown): value is ImportFailureItem['itemType'] =>
  value === 'file' || value === 'folder';

const parseFailureItem = (value: unknown): ImportFailureItem | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const { itemType, category, reason, sourceName, message } = value;

  if (!isFailureItemType(itemType) || typeof category !== 'string' || typeof reason !== 'string') {
    return undefined;
  }

  return {
    itemType,
    category: isPerItemFailureCategory(category) ? category : 'unknown',
    reason,
    ...(typeof sourceName === 'string' ? { sourceName } : {}),
    ...(typeof message === 'string' ? { message } : {}),
  };
};

const parseFailureItems = (value: unknown): ImportFailureItem[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const items = value.flatMap((entry) => {
    const item = parseFailureItem(entry);

    return item ? [item] : [];
  });

  return items.length > 0 ? items : undefined;
};

const parseTermination = (value: unknown): ImportJobTermination | null | undefined => {
  if (value === null) {
    return null;
  }

  if (!isRecord(value)) {
    return undefined;
  }

  const { reason, category } = value;

  // Keep unknown categories — `getTerminationCopy` has a generic Stopped fallback.
  // Dropping them would hide the Stopped row until the FE allowlist catches up.
  if (typeof reason !== 'string' || typeof category !== 'string' || category.length === 0) {
    return undefined;
  }

  return { reason, category };
};

const parseFailures = (value: unknown): Record<string, number> | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const entries = Object.entries(value).filter(
    (entry): entry is [string, number] => typeof entry[1] === 'number' && Number.isFinite(entry[1]),
  );

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

/**
 * Narrow additive reliability fields from published `@air/api` task data.
 * Runtime payloads may already include these before the client package types do.
 */
export const getImporterReliabilityData = (data: unknown): ImporterReliabilityData => {
  if (!isRecord(data)) {
    return {};
  }

  return {
    skippedAlreadyImported: readOptionalNumber(data, 'skippedAlreadyImported'),
    skippedExcluded: readOptionalNumber(data, 'skippedExcluded'),
    skippedZeroByte: readOptionalNumber(data, 'skippedZeroByte'),
    skippedUnsupported: readOptionalNumber(data, 'skippedUnsupported'),
    failureItems: parseFailureItems(data.failureItems),
    termination: parseTermination(data.termination),
    fileImportError: readOptionalNumber(data, 'fileImportError'),
    folderImportError: readOptionalNumber(data, 'folderImportError'),
    filesImportedCount: readOptionalNumber(data, 'filesImportedCount'),
    foldersImported: readOptionalNumber(data, 'foldersImported'),
    totalFilesFound: readOptionalNumber(data, 'totalFilesFound'),
    totalFoldersFound: readOptionalNumber(data, 'totalFoldersFound'),
    failures: parseFailures(data.failures),
  };
};

export type SkipReasonKey = 'skippedAlreadyImported' | 'skippedExcluded' | 'skippedZeroByte' | 'skippedUnsupported';

export type SkipBreakdown = {
  key: SkipReasonKey;
  label: string;
  count: number;
};

const SKIP_REASON_KEYS: readonly SkipReasonKey[] = [
  'skippedAlreadyImported',
  'skippedExcluded',
  'skippedZeroByte',
  'skippedUnsupported',
];

const SKIP_LABELS: Record<SkipReasonKey, string> = {
  skippedAlreadyImported: 'Already imported',
  skippedExcluded: 'Excluded',
  skippedZeroByte: 'Zero-byte',
  skippedUnsupported: 'Unsupported type',
};

export const getSkipBreakdown = (data: ImporterReliabilityData): SkipBreakdown[] =>
  SKIP_REASON_KEYS.map((key) => ({
    key,
    label: SKIP_LABELS[key],
    count: data[key] ?? 0,
  })).filter((row) => row.count > 0);

export const getTotalSkipped = (data: ImporterReliabilityData): number =>
  SKIP_REASON_KEYS.reduce((sum, key) => sum + (data[key] ?? 0), 0);

export const getTotalFailed = (data: ImporterReliabilityData): number =>
  (data.fileImportError ?? 0) + (data.folderImportError ?? 0);

const CATEGORY_ORDER: readonly PerItemFailureCategory[] = [
  'retryableTransient',
  'retryableTimeout',
  'terminalPermission',
  'terminalBadSourceData',
  'unknown',
];

const CATEGORY_LABELS: Record<PerItemFailureCategory, string> = {
  retryableTransient: 'Transient errors',
  retryableTimeout: 'Timeouts',
  terminalPermission: 'Permission issues',
  terminalBadSourceData: 'Bad source data',
  unknown: 'Unknown errors',
};

const CATEGORY_NEXT_STEPS: Record<PerItemFailureCategory, string> = {
  retryableTransient: 'Retry the import, or re-run just the failed files if you can',
  retryableTimeout: 'Retry, or split into smaller chunks',
  terminalPermission: 'Fix access at the source, then re-run',
  terminalBadSourceData: 'Nothing to retry — review the list',
  unknown: 'Contact support with the import id',
};

export type FailureCategoryGroup = {
  category: PerItemFailureCategory;
  label: string;
  nextStep: string;
  count: number;
  reasonCounts: { reason: string; label: string; count: number }[];
};

export const groupFailureItemsByCategory = (items: ImportFailureItem[]): FailureCategoryGroup[] => {
  const byCategory = new Map<PerItemFailureCategory, { count: number; reasons: Map<string, number> }>();

  items.forEach((item) => {
    const category = isPerItemFailureCategory(item.category) ? item.category : 'unknown';
    const existing = byCategory.get(category) ?? { count: 0, reasons: new Map<string, number>() };
    existing.count += 1;
    existing.reasons.set(item.reason, (existing.reasons.get(item.reason) ?? 0) + 1);
    byCategory.set(category, existing);
  });

  return CATEGORY_ORDER.flatMap((category) => {
    const entry = byCategory.get(category);

    if (!entry) {
      return [];
    }

    return [
      {
        category,
        label: CATEGORY_LABELS[category],
        nextStep: CATEGORY_NEXT_STEPS[category],
        count: entry.count,
        reasonCounts: [...entry.reasons.entries()]
          .map(([reason, count]) => ({
            reason,
            label: formatFailureReason(reason),
            count,
          }))
          .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
      },
    ];
  });
};

/**
 * Detail list is capped at `MAX_IMPORT_FAILURE_ITEMS`; aggregates (`fileImportError` etc.) are not.
 * Also treat an explicit `jobTooManyFailures` termination as capped.
 */
export const getFailureListCapNote = ({
  failureItems,
  totalFailed,
  termination,
}: {
  failureItems: ImportFailureItem[];
  totalFailed: number;
  termination?: ImportJobTermination | null;
}): string | undefined => {
  const listed = failureItems.length;

  if (listed === 0) {
    return undefined;
  }

  const cappedByBreaker = termination?.category === 'jobTooManyFailures';
  // Cap note only when the aggregate proves the list is incomplete — hitting
  // MAX with totalFailed === listed means we have every recorded failure.
  const cappedByLength = listed < totalFailed;

  if (!cappedByBreaker && !cappedByLength) {
    return undefined;
  }

  const shown = listed.toLocaleString();

  if (totalFailed > listed) {
    return `Showing first ${shown} of ${totalFailed.toLocaleString()} failures`;
  }

  if (listed >= MAX_IMPORT_FAILURE_ITEMS) {
    return `Showing first ${shown} failures (list capped at ${MAX_IMPORT_FAILURE_ITEMS.toLocaleString()})`;
  }

  return `Showing first ${shown} failures (list capped)`;
};

const TERMINATION_COPY: Record<JobTerminationCategory, { label: string; nextStep: string }> = {
  jobTooManyFailures: {
    label: 'Stopped early — too many item failures',
    nextStep: 'Fix the systemic issue (access or source), then re-run',
  },
  jobCapacity: {
    label: 'Stopped — workspace out of storage',
    nextStep: 'Free space or upgrade your plan, then re-run',
  },
  jobSession: {
    label: 'Stopped — source session expired',
    nextStep: 'Reconnect the source, then re-run',
  },
  jobPermission: {
    label: 'Stopped — lost workspace access',
    nextStep: 'Restore access, then re-run',
  },
  jobTimeout: {
    label: 'Stopped — import timed out',
    nextStep: 'Split into smaller chunks, then re-run',
  },
  jobResource: {
    label: 'Stopped — ran out of memory',
    nextStep: 'Split into smaller chunks; contact support if it recurs',
  },
  jobMaxAttempts: {
    label: 'Stopped — exhausted automatic retries',
    nextStep: 'Review the log or contact support',
  },
};

export const getTerminationCopy = (
  termination: ImportJobTermination | null | undefined,
): { label: string; nextStep: string } | undefined => {
  if (!termination) {
    return undefined;
  }

  if (isJobTerminationCategory(termination.category)) {
    return TERMINATION_COPY[termination.category];
  }

  return {
    label: `Stopped — ${formatFailureReason(termination.reason)}`,
    nextStep: 'Contact support with the import id',
  };
};

/**
 * Spreadsheet apps (notably Excel) strip leading whitespace/controls before
 * deciding if a cell is a formula — so check the first significant character,
 * not only `value[0]` (OWASP CSV Injection / CWE-1236).
 */
const CSV_FORMULA_TRIGGERS = new Set(['=', '+', '-', '@']);
const CSV_LEADING_STRIP_CHARS = new Set([' ', '\t', '\r', '\n', '\v', '\f', '\0']);

const escapeCsvValue = (value: string): string => {
  let probe = 0;
  while (probe < value.length && CSV_LEADING_STRIP_CHARS.has(value[probe] ?? '')) {
    probe += 1;
  }

  const significant = value[probe] ?? '';
  const needsFormulaNeutralization = CSV_FORMULA_TRIGGERS.has(significant);
  const safeValue = needsFormulaNeutralization ? `'${value}` : value;

  if (
    needsFormulaNeutralization ||
    safeValue.includes('"') ||
    safeValue.includes(',') ||
    safeValue.includes('\n') ||
    safeValue.includes('\r')
  ) {
    return `"${safeValue.replace(/"/g, '""')}"`;
  }

  return safeValue;
};

export const buildImportFailureItemsCsv = (items: ImportFailureItem[]): string => {
  const header = 'Item Type,Category,Reason,Name,Message';
  const rows = items.map((item) =>
    [item.itemType, item.category, item.reason, item.sourceName ?? '', item.message ?? '']
      .map((value) => escapeCsvValue(value))
      .join(','),
  );

  return [header, ...rows].join('\r\n');
};

export const downloadImportFailureItemsCsv = (
  items: ImportFailureItem[],
  filename = 'import-failed-items.csv',
): void => {
  const csv = buildImportFailureItemsCsv(items);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
